import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { simulationLevelsAPI, usersAPI, institutionsAPI } from '../../api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  GraduationCap,
  Users,
  Buildings,
  Plus,
  Pencil,
  Trash,
  Check,
  X,
  Clock,
  ShieldCheck,
  UserCircle,
  ListChecks,
  CaretRight,
  Info
} from '@phosphor-icons/react';

const LEVEL_COLORS = {
  frecuente: 'bg-blue-100 text-blue-800 border-blue-300',
  patologica: 'bg-purple-100 text-purple-800 border-purple-300',
  interactiva: 'bg-green-100 text-green-800 border-green-300'
};

const LEVEL_ICONS = {
  frecuente: <ListChecks size={18} />,
  patologica: <GraduationCap size={18} />,
  interactiva: <Users size={18} />
};

export default function SimulationLevelsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('institution');
  
  // Data states
  const [levelsInfo, setLevelsInfo] = useState([]);
  const [institutionLevels, setInstitutionLevels] = useState([]);
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  
  // Modal states
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Form states
  const [groupForm, setGroupForm] = useState({ nombre: '', descripcion: '', niveles_habilitados: [] });
  const [userLevelsForm, setUserLevelsForm] = useState({ niveles: [], grupo_id: '' });

  const institutionId = user?.institution_id || 'tenant-demo-001';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [levelsRes, instLevelsRes, groupsRes, usersRes] = await Promise.all([
        simulationLevelsAPI.getLevelsInfo(),
        simulationLevelsAPI.getInstitutionLevels(institutionId),
        simulationLevelsAPI.getGroups(institutionId),
        usersAPI.getAll()
      ]);
      
      setLevelsInfo(levelsRes.data.niveles || []);
      setInstitutionLevels(instLevelsRes.data.niveles_habilitados || []);
      setGroups(groupsRes.data || []);
      setUsers(usersRes.data || []);
      
      // Load audit log
      try {
        const auditRes = await simulationLevelsAPI.getAuditLog(institutionId);
        setAuditLog(auditRes.data || []);
      } catch {
        // Audit data may not exist yet for new institutions
      }
    } catch {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleInstitutionLevel = async (level) => {
    const newLevels = institutionLevels.includes(level)
      ? institutionLevels.filter(l => l !== level)
      : [...institutionLevels, level];
    
    try {
      await simulationLevelsAPI.updateInstitutionLevels(institutionId, newLevels);
      setInstitutionLevels(newLevels);
      toast.success('Niveles actualizados');
      loadData();
    } catch (error) {
      toast.error('Error al actualizar niveles');
    }
  };

  const handleCreateGroup = async () => {
    try {
      await simulationLevelsAPI.createGroup(institutionId, groupForm);
      toast.success('Grupo creado');
      setShowGroupModal(false);
      setGroupForm({ nombre: '', descripcion: '', niveles_habilitados: [] });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear grupo');
    }
  };

  const handleUpdateGroup = async () => {
    try {
      await simulationLevelsAPI.updateGroup(editingGroup.id, groupForm);
      toast.success('Grupo actualizado');
      setShowGroupModal(false);
      setEditingGroup(null);
      setGroupForm({ nombre: '', descripcion: '', niveles_habilitados: [] });
      loadData();
    } catch (error) {
      toast.error('Error al actualizar grupo');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!confirm('¿Eliminar este grupo? Los usuarios serán desasignados.')) return;
    try {
      await simulationLevelsAPI.deleteGroup(groupId);
      toast.success('Grupo eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar grupo');
    }
  };

  const handleAssignUserLevels = async () => {
    try {
      const data = {
        niveles_habilitados: userLevelsForm.niveles.length > 0 ? userLevelsForm.niveles : undefined,
        grupo_id: userLevelsForm.grupo_id || undefined
      };
      await simulationLevelsAPI.assignUserLevels(selectedUser.id, data);
      toast.success('Niveles asignados');
      setShowUserModal(false);
      setSelectedUser(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar niveles');
    }
  };

  const openEditGroup = (group) => {
    setEditingGroup(group);
    setGroupForm({
      nombre: group.nombre,
      descripcion: group.descripcion || '',
      niveles_habilitados: group.niveles_habilitados || []
    });
    setShowGroupModal(true);
  };

  const openAssignUser = async (userItem) => {
    setSelectedUser(userItem);
    try {
      const res = await simulationLevelsAPI.getUserLevels(userItem.id);
      setUserLevelsForm({
        niveles: res.data.niveles_directos || [],
        grupo_id: res.data.grupo_id || ''
      });
    } catch (e) {
      setUserLevelsForm({ niveles: [], grupo_id: '' });
    }
    setShowUserModal(true);
  };

  const toggleGroupLevel = (level) => {
    setGroupForm(prev => ({
      ...prev,
      niveles_habilitados: prev.niveles_habilitados.includes(level)
        ? prev.niveles_habilitados.filter(l => l !== level)
        : [...prev.niveles_habilitados, level]
    }));
  };

  const toggleUserLevel = (level) => {
    setUserLevelsForm(prev => ({
      ...prev,
      niveles: prev.niveles.includes(level)
        ? prev.niveles.filter(l => l !== level)
        : [...prev.niveles, level]
    }));
  };

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" data-testid="simulation-levels-title">
            Gestión de Niveles de Simulación
          </h1>
          <p className="text-slate-500 mt-1">
            Configure los niveles de simulación disponibles para su institución y usuarios
          </p>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {levelsInfo.map(level => (
            <Card key={level.id} className={`border-2 ${institutionLevels.includes(level.id) ? 'border-blue-500 bg-blue-50/50' : 'border-gray-200'}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {LEVEL_ICONS[level.id]}
                    <CardTitle className="text-lg">{level.nombre}</CardTitle>
                  </div>
                  <Badge className={LEVEL_COLORS[level.id]}>
                    {level.tipo_evaluacion}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-3">{level.descripcion}</p>
                <div className="flex flex-wrap gap-1">
                  {level.caracteristicas?.map((c, i) => (
                    <Badge key={i} variant="outline" className="text-xs">{c}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 w-full max-w-2xl">
            <TabsTrigger value="institution" className="flex items-center gap-2">
              <Buildings size={16} /> Institución
            </TabsTrigger>
            <TabsTrigger value="groups" className="flex items-center gap-2">
              <Users size={16} /> Grupos
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <UserCircle size={16} /> Usuarios
            </TabsTrigger>
            <TabsTrigger value="audit" className="flex items-center gap-2">
              <Clock size={16} /> Auditoría
            </TabsTrigger>
          </TabsList>

          {/* Institution Tab */}
          <TabsContent value="institution" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldCheck size={20} />
                  Niveles Habilitados en la Institución
                </CardTitle>
                <CardDescription>
                  Seleccione qué niveles de simulación están disponibles para toda la institución.
                  Los grupos y usuarios solo podrán acceder a niveles aquí habilitados.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {levelsInfo.map(level => (
                    <div 
                      key={level.id}
                      className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        institutionLevels.includes(level.id) 
                          ? 'border-green-500 bg-green-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => handleToggleInstitutionLevel(level.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          institutionLevels.includes(level.id) ? 'bg-green-500 text-white' : 'bg-gray-200'
                        }`}>
                          {institutionLevels.includes(level.id) ? <Check size={20} /> : LEVEL_ICONS[level.id]}
                        </div>
                        <div>
                          <h4 className="font-semibold">{level.nombre}</h4>
                          <p className="text-sm text-slate-500">{level.descripcion}</p>
                        </div>
                      </div>
                      <Badge className={LEVEL_COLORS[level.id]}>
                        {institutionLevels.includes(level.id) ? 'Habilitado' : 'Deshabilitado'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Groups Tab */}
          <TabsContent value="groups" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Grupos de Simulación</CardTitle>
                  <CardDescription>
                    Cree grupos para asignar niveles a múltiples usuarios (ej: "Internos 6° año", "Enfermería 3er semestre")
                  </CardDescription>
                </div>
                <Button onClick={() => { setEditingGroup(null); setGroupForm({ nombre: '', descripcion: '', niveles_habilitados: [] }); setShowGroupModal(true); }}>
                  <Plus size={16} className="mr-2" /> Nuevo Grupo
                </Button>
              </CardHeader>
              <CardContent>
                {groups.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Users size={48} className="mx-auto mb-2 opacity-50" />
                    <p>No hay grupos creados</p>
                    <p className="text-sm">Cree un grupo para asignar niveles a múltiples usuarios</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {groups.map(group => (
                      <div key={group.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50">
                        <div>
                          <h4 className="font-semibold">{group.nombre}</h4>
                          <p className="text-sm text-slate-500">{group.descripcion}</p>
                          <div className="flex gap-2 mt-2">
                            {group.niveles_habilitados?.map(level => (
                              <Badge key={level} className={LEVEL_COLORS[level]}>{level}</Badge>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">{group.user_count || 0}</div>
                            <div className="text-xs text-slate-500">usuarios</div>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="icon" onClick={() => openEditGroup(group)}>
                              <Pencil size={16} />
                            </Button>
                            <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDeleteGroup(group.id)}>
                              <Trash size={16} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Asignación por Usuario</CardTitle>
                <CardDescription>
                  Asigne niveles individualmente o mediante grupos. Los instructores y administradores tienen acceso a todos los niveles automáticamente.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {users.filter(u => u.role === 'alumno').map(userItem => (
                      <div key={userItem.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                            <UserCircle size={24} className="text-slate-500" />
                          </div>
                          <div>
                            <h4 className="font-medium">{userItem.nombre} {userItem.apellido}</h4>
                            <p className="text-sm text-slate-500">{userItem.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {userItem.niveles_simulacion?.length > 0 ? (
                            <div className="flex gap-1">
                              {userItem.niveles_simulacion.map(level => (
                                <Badge key={level} className={LEVEL_COLORS[level]} variant="outline">{level}</Badge>
                              ))}
                            </div>
                          ) : userItem.grupo_simulacion_id ? (
                            <Badge variant="secondary">En grupo</Badge>
                          ) : (
                            <Badge variant="outline" className="text-orange-600 border-orange-300">Sin asignar</Badge>
                          )}
                          <Button variant="outline" size="sm" onClick={() => openAssignUser(userItem)}>
                            Asignar
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Tab */}
          <TabsContent value="audit" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Registro de Auditoría</CardTitle>
                <CardDescription>
                  Historial de cambios en asignaciones de niveles de simulación
                </CardDescription>
              </CardHeader>
              <CardContent>
                {auditLog.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Clock size={48} className="mx-auto mb-2 opacity-50" />
                    <p>No hay registros de auditoría</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-3">
                      {auditLog.map(log => (
                        <div key={log.id} className="p-3 border rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Badge variant={log.action === 'assign' ? 'default' : 'secondary'}>
                                {log.action}
                              </Badge>
                              <span className="font-medium">{log.target_name || log.target_id}</span>
                              <span className="text-slate-500">({log.target_type})</span>
                            </div>
                            <span className="text-sm text-slate-500">
                              {new Date(log.changed_at).toLocaleString('es-CL')}
                            </span>
                          </div>
                          <div className="mt-2 text-sm">
                            <span className="text-slate-500">Por: </span>
                            <span>{log.changed_by_name || log.changed_by}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-sm">
                            <span className="text-slate-500">Cambio:</span>
                            <span className="text-red-500 line-through">{log.previous_levels?.join(', ') || 'ninguno'}</span>
                            <CaretRight size={14} />
                            <span className="text-green-600">{log.new_levels?.join(', ') || 'ninguno'}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Group Modal */}
        <Dialog open={showGroupModal} onOpenChange={setShowGroupModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingGroup ? 'Editar Grupo' : 'Nuevo Grupo'}</DialogTitle>
              <DialogDescription>
                {editingGroup ? 'Modifique los datos del grupo' : 'Complete los datos para crear un nuevo grupo de simulación'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Nombre del grupo</Label>
                <Input 
                  value={groupForm.nombre}
                  onChange={e => setGroupForm(prev => ({ ...prev, nombre: e.target.value }))}
                  placeholder="Ej: Internos 6° año"
                />
              </div>
              <div>
                <Label>Descripción (opcional)</Label>
                <Input 
                  value={groupForm.descripcion}
                  onChange={e => setGroupForm(prev => ({ ...prev, descripcion: e.target.value }))}
                  placeholder="Descripción del grupo"
                />
              </div>
              <div>
                <Label className="mb-2 block">Niveles habilitados</Label>
                <div className="space-y-2">
                  {levelsInfo.filter(l => institutionLevels.includes(l.id)).map(level => (
                    <div 
                      key={level.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${
                        groupForm.niveles_habilitados.includes(level.id) ? 'bg-blue-50 border-blue-300' : 'hover:bg-slate-50'
                      }`}
                      onClick={() => toggleGroupLevel(level.id)}
                    >
                      <Checkbox checked={groupForm.niveles_habilitados.includes(level.id)} />
                      <div>
                        <span className="font-medium">{level.nombre}</span>
                        <p className="text-sm text-slate-500">{level.descripcion}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGroupModal(false)}>Cancelar</Button>
              <Button onClick={editingGroup ? handleUpdateGroup : handleCreateGroup}>
                {editingGroup ? 'Guardar' : 'Crear Grupo'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* User Assignment Modal */}
        <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Asignar Niveles a Usuario</DialogTitle>
              <DialogDescription>
                {selectedUser && `Configurar acceso para ${selectedUser.nombre} ${selectedUser.apellido}`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-3 bg-blue-50 rounded-lg flex items-start gap-2">
                <Info size={20} className="text-blue-600 mt-0.5" />
                <p className="text-sm text-blue-800">
                  Puede asignar niveles directamente o mediante un grupo. La asignación directa tiene prioridad.
                </p>
              </div>
              
              <div>
                <Label className="mb-2 block">Asignar a grupo</Label>
                <Select value={userLevelsForm.grupo_id || "none"} onValueChange={v => setUserLevelsForm(prev => ({ ...prev, grupo_id: v === "none" ? "" : v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar grupo (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin grupo</SelectItem>
                    {groups.map(group => (
                      <SelectItem key={group.id} value={group.id}>
                        {group.nombre} ({group.niveles_habilitados?.join(', ')})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="mb-2 block">O asignar niveles directamente</Label>
                <div className="space-y-2">
                  {levelsInfo.filter(l => institutionLevels.includes(l.id)).map(level => (
                    <div 
                      key={level.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${
                        userLevelsForm.niveles.includes(level.id) ? 'bg-blue-50 border-blue-300' : 'hover:bg-slate-50'
                      }`}
                      onClick={() => toggleUserLevel(level.id)}
                    >
                      <Checkbox checked={userLevelsForm.niveles.includes(level.id)} />
                      <div>
                        <span className="font-medium">{level.nombre}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUserModal(false)}>Cancelar</Button>
              <Button onClick={handleAssignUserLevels}>Asignar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
